// src/pages/AdminAffiliatePayouts.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

const PAGE_SIZE = 10;

function formatMoney(value) {
  const n = Number(value || 0);
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

function normalizeText(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function toDateSafe(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  return null;
}

function formatDate(value) {
  const d = toDateSafe(value);
  if (!d) return "—";
  return d.toLocaleString("fr-FR");
}

function earningStatusLabel(status) {
  if (status === "active_commission_tracking") return "Commission active";
  if (status === "pending_vendor_payment") return "En attente paiement vendeur";
  if (status === "payable") return "À payer";
  if (status === "paid") return "Payé";
  if (status === "cancelled") return "Annulé";
  return status || "—";
}

function earningTypeLabel(item) {
  if (item?.earningType === "commission_based") {
    return `${Number(item?.percentOnGoShopCommission || 0)}% des commissions GoShop`;
  }

  if (item?.earningType === "fixed_pack_bonus") {
    return `Prime fixe ${formatMoney(item?.earningAmount || 0)}`;
  }

  return item?.earningLabel || "Gain affilié";
}

function statusClass(status) {
  if (status === "paid") return "aap-status-paid";
  if (status === "payable") return "aap-status-payable";
  if (status === "pending_vendor_payment") return "aap-status-pending";
  if (status === "active_commission_tracking") return "aap-status-active";
  if (status === "cancelled") return "aap-status-cancelled";
  return "aap-status-default";
}

export default function AdminAffiliatePayouts() {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [previewItem, setPreviewItem] = useState(null);

  const loadEarnings = async () => {
    setLoading(true);
    setError(null);

    try {
      const qRef = query(collection(db, "affiliateEarnings"), orderBy("createdAt", "desc"));
      const snap = await getDocs(qRef);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEarnings(list);
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les gains affiliés.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEarnings();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, typeFilter]);

  const updateEarningStatus = async (item, nextStatus) => {
    const labels = {
      payable: "rendre ce gain payable",
      paid: "marquer ce gain comme payé",
      cancelled: "annuler ce gain",
      pending_vendor_payment: "remettre ce gain en attente paiement vendeur",
    };

    if (!window.confirm(`Voulez-vous ${labels[nextStatus] || "mettre à jour ce gain"} ?`)) {
      return;
    }

    setActionLoadingId(item.id);
    setError(null);
    setInfo(null);

    try {
      const updatePayload = {
        earningStatus: nextStatus,
        updatedAt: serverTimestamp(),
      };

      if (nextStatus === "payable") {
        updatePayload.payableAt = serverTimestamp();
      }

      if (nextStatus === "paid") {
        updatePayload.paidAt = serverTimestamp();
        updatePayload.payoutStatus = "paid";
      }

      if (nextStatus === "cancelled") {
        updatePayload.cancelledAt = serverTimestamp();
      }

      await updateDoc(doc(db, "affiliateEarnings", item.id), updatePayload);

      setInfo("Gain affilié mis à jour ✅");
      setPreviewItem(null);
      await loadEarnings();
    } catch (err) {
      console.error(err);
      setError("Impossible de mettre à jour ce gain affilié.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredEarnings = useMemo(() => {
    const needle = normalizeText(search);

    return earnings.filter((item) => {
      const statusOk = statusFilter === "all" ? true : item.earningStatus === statusFilter;
      const typeOk = typeFilter === "all" ? true : item.earningType === typeFilter;

      if (!statusOk || !typeOk) return false;
      if (!needle) return true;

      const haystack = normalizeText(
        [
          item.affiliateCode,
          item.affiliateOwnerUid,
          item.newVendorShopName,
          item.newVendorUid,
          item.packName,
          item.packId,
          item.earningStatus,
          item.earningType,
          item.earningLabel,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(needle);
    });
  }, [earnings, search, statusFilter, typeFilter]);

  const stats = useMemo(() => {
    const payable = earnings.filter((x) => x.earningStatus === "payable");
    const paid = earnings.filter((x) => x.earningStatus === "paid");
    const pending = earnings.filter((x) => x.earningStatus === "pending_vendor_payment");
    const active = earnings.filter((x) => x.earningStatus === "active_commission_tracking");

    const sum = (list) => list.reduce((s, x) => s + Number(x.earningAmount || 0), 0);

    return {
      total: earnings.length,
      payableCount: payable.length,
      payableAmount: sum(payable),
      paidCount: paid.length,
      paidAmount: sum(paid),
      pendingCount: pending.length,
      pendingAmount: sum(pending),
      activeCount: active.length,
    };
  }, [earnings]);

  const totalPages = Math.max(1, Math.ceil(filteredEarnings.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const visibleEarnings = filteredEarnings.slice(start, end);

  return (
    <div className="aap-page">
      <main className="aap-main">
        <header className="aap-header">
          <div>
            <h1 className="aap-title">Paiements affiliés</h1>
            <p className="aap-subtitle">
              Suivez les gains des ambassadeurs GoShop, validez les montants payables et marquez les paiements effectués.
            </p>
          </div>

          <button className="aap-refreshBtn" type="button" onClick={loadEarnings} disabled={loading}>
            {loading ? "Chargement..." : "Actualiser"}
          </button>
        </header>

        {error && <div className="aap-alert aap-alert-error">{error}</div>}
        {info && <div className="aap-alert aap-alert-success">{info}</div>}

        <section className="aap-stats">
          <div className="aap-statCard">
            <span>Total gains</span>
            <strong>{stats.total}</strong>
          </div>
          <div className="aap-statCard aap-statCard-payable">
            <span>À payer</span>
            <strong>{formatMoney(stats.payableAmount)}</strong>
            <small>{stats.payableCount} gain(s)</small>
          </div>
          <div className="aap-statCard aap-statCard-paid">
            <span>Déjà payé</span>
            <strong>{formatMoney(stats.paidAmount)}</strong>
            <small>{stats.paidCount} gain(s)</small>
          </div>
          <div className="aap-statCard aap-statCard-pending">
            <span>En attente vendeur</span>
            <strong>{formatMoney(stats.pendingAmount)}</strong>
            <small>{stats.pendingCount} gain(s)</small>
          </div>
          <div className="aap-statCard aap-statCard-active">
            <span>Commissions actives</span>
            <strong>{stats.activeCount}</strong>
          </div>
        </section>

        <section className="aap-toolbarCard">
          <div className="aap-toolbar">
            <input
              className="aap-search"
              type="search"
              placeholder="Rechercher affilié, vendeur, pack, code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select className="aap-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous les statuts</option>
              <option value="pending_vendor_payment">En attente paiement vendeur</option>
              <option value="active_commission_tracking">Commission active</option>
              <option value="payable">À payer</option>
              <option value="paid">Payé</option>
              <option value="cancelled">Annulé</option>
            </select>

            <select className="aap-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">Tous les types</option>
              <option value="fixed_pack_bonus">Prime fixe</option>
              <option value="commission_based">Commission sur activité</option>
            </select>
          </div>

          <div className="aap-toolbarMeta">{filteredEarnings.length} gain(s) trouvé(s)</div>
        </section>

        <section className="aap-tableCard">
          {loading ? (
            <p className="aap-empty">Chargement des gains affiliés...</p>
          ) : filteredEarnings.length === 0 ? (
            <p className="aap-empty">Aucun gain affilié trouvé.</p>
          ) : (
            <>
              <div className="aap-tableWrap">
                <table className="aap-table">
                  <thead>
                    <tr>
                      <th>Affilié</th>
                      <th>Vendeur apporté</th>
                      <th>Pack</th>
                      <th>Gain</th>
                      <th>Statut</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleEarnings.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="aap-mainText">{item.affiliateCode || "—"}</div>
                          <div className="aap-subText">{item.affiliateOwnerUid || "UID affilié non renseigné"}</div>
                        </td>

                        <td>
                          <div className="aap-mainText">{item.newVendorShopName || "—"}</div>
                          <div className="aap-subText">{item.newVendorUid || "UID vendeur non renseigné"}</div>
                        </td>

                        <td>
                          <div className="aap-mainText">{item.packName || item.packId || "—"}</div>
                          <div className="aap-subText">{item.packId || "—"}</div>
                        </td>

                        <td>
                          <div className="aap-mainText">{earningTypeLabel(item)}</div>
                          {item.earningType === "fixed_pack_bonus" && (
                            <div className="aap-amountText">{formatMoney(item.earningAmount || 0)}</div>
                          )}
                        </td>

                        <td>
                          <span className={`aap-status ${statusClass(item.earningStatus)}`}>
                            {earningStatusLabel(item.earningStatus)}
                          </span>
                        </td>

                        <td>
                          <div className="aap-subText">Créé : {formatDate(item.createdAt)}</div>
                          {item.paidAt && <div className="aap-subText">Payé : {formatDate(item.paidAt)}</div>}
                        </td>

                        <td>
                          <div className="aap-actions">
                            <button type="button" className="aap-outlineBtn" onClick={() => setPreviewItem(item)}>
                              Détails
                            </button>

                            {item.earningStatus === "pending_vendor_payment" && item.earningType === "fixed_pack_bonus" && (
                              <button
                                type="button"
                                className="aap-primaryBtn"
                                disabled={actionLoadingId === item.id}
                                onClick={() => updateEarningStatus(item, "payable")}
                              >
                                Rendre payable
                              </button>
                            )}

                            {item.earningStatus === "payable" && (
                              <button
                                type="button"
                                className="aap-successBtn"
                                disabled={actionLoadingId === item.id}
                                onClick={() => updateEarningStatus(item, "paid")}
                              >
                                Marquer payé
                              </button>
                            )}

                            {item.earningStatus !== "paid" && item.earningStatus !== "cancelled" && (
                              <button
                                type="button"
                                className="aap-dangerBtn"
                                disabled={actionLoadingId === item.id}
                                onClick={() => updateEarningStatus(item, "cancelled")}
                              >
                                Annuler
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="aap-pagination">
                <div>
                  Affichage de <strong>{start + 1}</strong> à <strong>{Math.min(end, filteredEarnings.length)}</strong> sur <strong>{filteredEarnings.length}</strong>
                </div>

                <div className="aap-paginationControls">
                  <button type="button" disabled={safePage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                    ← Précédent
                  </button>
                  <span>Page {safePage} / {totalPages}</span>
                  <button type="button" disabled={safePage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
                    Suivant →
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        {previewItem && (
          <div className="aap-modalBackdrop" onMouseDown={() => setPreviewItem(null)}>
            <div className="aap-modal" onMouseDown={(e) => e.stopPropagation()}>
              <div className="aap-modalHeader">
                <div>
                  <div className="aap-modalTitle">Détail du gain affilié</div>
                  <div className="aap-modalSub">{previewItem.affiliateCode || "—"}</div>
                </div>
                <button className="aap-closeBtn" type="button" onClick={() => setPreviewItem(null)}>✕</button>
              </div>

              <div className="aap-modalBody">
                <div className="aap-detailGrid">
                  <Detail label="Affilié" value={previewItem.affiliateCode} />
                  <Detail label="UID affilié" value={previewItem.affiliateOwnerUid} />
                  <Detail label="Vendeur apporté" value={previewItem.newVendorShopName} />
                  <Detail label="UID vendeur" value={previewItem.newVendorUid} />
                  <Detail label="Pack" value={previewItem.packName || previewItem.packId} />
                  <Detail label="Type de gain" value={earningTypeLabel(previewItem)} />
                  <Detail label="Montant" value={formatMoney(previewItem.earningAmount || 0)} />
                  <Detail label="Statut" value={earningStatusLabel(previewItem.earningStatus)} />
                  <Detail label="Créé le" value={formatDate(previewItem.createdAt)} />
                  <Detail label="Payable le" value={formatDate(previewItem.payableAt)} />
                  <Detail label="Payé le" value={formatDate(previewItem.paidAt)} />
                </div>

                <div className="aap-modalActions">
                  {previewItem.earningStatus === "pending_vendor_payment" && previewItem.earningType === "fixed_pack_bonus" && (
                    <button className="aap-primaryBtn" type="button" onClick={() => updateEarningStatus(previewItem, "payable")}>Rendre payable</button>
                  )}

                  {previewItem.earningStatus === "payable" && (
                    <button className="aap-successBtn" type="button" onClick={() => updateEarningStatus(previewItem, "paid")}>Marquer payé</button>
                  )}

                  <button className="aap-outlineBtn" type="button" onClick={() => setPreviewItem(null)}>Fermer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .aap-page{
            min-height:100vh;
            background:#f8fafc;
            padding:24px 16px 40px;
            display:flex;
            justify-content:center;
            color:#0f172a;
            font-family:Arial, sans-serif;
          }

          .aap-main{width:100%;max-width:1180px;}
          .aap-header{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-bottom:16px;}
          .aap-title{font-size:28px;font-weight:900;margin:0 0 6px;}
          .aap-subtitle{font-size:13px;color:#64748b;margin:0;max-width:760px;}

          .aap-refreshBtn,.aap-primaryBtn,.aap-successBtn,.aap-dangerBtn,.aap-outlineBtn{
            border-radius:999px;
            padding:8px 13px;
            font-size:12px;
            font-weight:800;
            cursor:pointer;
            border:1px solid transparent;
            white-space:nowrap;
          }
          .aap-refreshBtn{background:#eff6ff;color:#1d4ed8;border-color:#bfdbfe;}
          .aap-primaryBtn{background:#f97316;color:#fff;border-color:#f97316;}
          .aap-successBtn{background:#16a34a;color:#fff;border-color:#16a34a;}
          .aap-dangerBtn{background:#fff1f2;color:#b91c1c;border-color:#fecdd3;}
          .aap-outlineBtn{background:#fff;color:#0f172a;border-color:#e5e7eb;}
          button:disabled{opacity:.6;cursor:not-allowed;}

          .aap-alert{margin-bottom:12px;padding:10px 12px;border-radius:12px;font-size:13px;font-weight:700;}
          .aap-alert-error{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;}
          .aap-alert-success{background:#ecfdf5;color:#166534;border:1px solid #bbf7d0;}

          .aap-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px;margin-bottom:14px;}
          .aap-statCard{background:#fff;border:1px solid #e5e7eb;border-radius:18px;padding:14px;box-shadow:0 10px 24px rgba(15,23,42,.05);}
          .aap-statCard span{display:block;font-size:12px;color:#64748b;font-weight:700;margin-bottom:6px;}
          .aap-statCard strong{font-size:20px;font-weight:900;color:#0f172a;display:block;}
          .aap-statCard small{display:block;color:#64748b;margin-top:4px;font-weight:700;}
          .aap-statCard-payable{background:linear-gradient(180deg,#fff7ed,#fff);}
          .aap-statCard-paid{background:linear-gradient(180deg,#ecfdf5,#fff);}
          .aap-statCard-pending{background:linear-gradient(180deg,#fef9c3,#fff);}
          .aap-statCard-active{background:linear-gradient(180deg,#eff6ff,#fff);}

          .aap-toolbarCard,.aap-tableCard{background:#fff;border:1px solid #e5e7eb;border-radius:22px;padding:14px;box-shadow:0 14px 30px rgba(15,23,42,.06);}
          .aap-toolbarCard{margin-bottom:14px;}
          .aap-toolbar{display:flex;gap:10px;align-items:center;flex-wrap:wrap;}
          .aap-search,.aap-filter{border:1px solid #d1d5db;border-radius:14px;padding:10px 12px;font-size:13px;outline:none;background:#fff;}
          .aap-search{flex:1;min-width:320px;}
          .aap-toolbarMeta{margin-top:10px;font-size:12px;color:#64748b;font-weight:800;}

          .aap-tableWrap{overflow-x:auto;}
          .aap-table{width:100%;min-width:1120px;border-collapse:collapse;font-size:13px;}
          .aap-table th{text-align:left;padding:10px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;font-size:12px;}
          .aap-table td{padding:12px 10px;border-bottom:1px solid #f1f5f9;vertical-align:top;}
          .aap-table tr:hover{background:#fafafa;}
          .aap-mainText{font-weight:900;color:#0f172a;}
          .aap-subText{font-size:12px;color:#64748b;margin-top:4px;max-width:230px;word-break:break-word;}
          .aap-amountText{font-size:12px;color:#16a34a;font-weight:900;margin-top:4px;}
          .aap-actions{display:flex;flex-direction:column;gap:7px;align-items:flex-start;}

          .aap-status{display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;font-size:11px;font-weight:900;}
          .aap-status-paid{background:#dcfce7;color:#166534;}
          .aap-status-payable{background:#ffedd5;color:#9a3412;}
          .aap-status-pending{background:#fef9c3;color:#92400e;}
          .aap-status-active{background:#dbeafe;color:#1d4ed8;}
          .aap-status-cancelled{background:#fee2e2;color:#b91c1c;}
          .aap-status-default{background:#f1f5f9;color:#475569;}

          .aap-empty{margin:0;color:#64748b;font-size:13px;padding:6px;}
          .aap-pagination{margin-top:14px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;font-size:12px;color:#64748b;}
          .aap-paginationControls{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
          .aap-paginationControls button{border:1px solid #d1d5db;background:#fff;border-radius:999px;padding:7px 12px;font-size:12px;font-weight:800;cursor:pointer;}

          .aap-modalBackdrop{position:fixed;inset:0;background:rgba(15,23,42,.62);display:flex;align-items:center;justify-content:center;padding:16px;z-index:9999;}
          .aap-modal{width:min(780px,100%);background:#fff;border-radius:22px;overflow:hidden;box-shadow:0 30px 80px rgba(0,0,0,.35);}
          .aap-modalHeader{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid #e5e7eb;}
          .aap-modalTitle{font-size:18px;font-weight:900;}
          .aap-modalSub{font-size:12px;color:#64748b;margin-top:3px;}
          .aap-closeBtn{width:38px;height:38px;border-radius:12px;border:1px solid #e5e7eb;background:#f8fafc;font-weight:900;cursor:pointer;}
          .aap-modalBody{padding:16px;}
          .aap-detailGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}
          .aap-detail{background:#f8fafc;border:1px solid #e5e7eb;border-radius:14px;padding:11px;}
          .aap-detail span{display:block;color:#64748b;font-size:11px;font-weight:900;text-transform:uppercase;margin-bottom:4px;}
          .aap-detail strong{display:block;color:#0f172a;font-size:13px;word-break:break-word;}
          .aap-modalActions{margin-top:16px;display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap;}

          @media(max-width:980px){.aap-stats{grid-template-columns:repeat(2,minmax(0,1fr));}.aap-search{min-width:100%;}}
          @media(max-width:620px){.aap-stats,.aap-detailGrid{grid-template-columns:1fr;}.aap-toolbar{flex-direction:column;align-items:stretch;}.aap-filter{width:100%;}}
        `}</style>
      </main>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="aap-detail">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}
